<template>
  <form class="search-form" @submit.prevent="onSubmit">
    <div class="row align-items-center mb-3">
      <div class="col-12 d-flex justify-content-end align-items-center gap-2">
        <AutoComplete :model-value="incDisplayValue" :suggestions="isSearching ? sapSuggestions : []" placeholder="Search INC / Item Name" fluid class="flex-grow-1 sap-autocomplete" input-class="p-inputtext-sm" @update:model-value="onIncInput" @complete="searchInc($event.query)" @item-select="onIncSelect"/>

        <select v-model="formData.item_type" class="form-select item-type-select" :disabled="!selectedIncObject" @change="handleTypeChange">
          <option value="">Select Item Type</option>
          <option v-for="opt in itemTypeOptions" :key="opt.id" :value="opt.code">{{ opt.code }}</option>
        </select>

        <AutoComplete v-if="companyCode === 'MIND-ID'" :model-value="clientDisplayValue" :suggestions="clientSuggestions"
          placeholder="Select Client" fluid input-class="p-inputtext-sm" @update:model-value="onClientInput"
          @complete="searchClients($event.query)" @item-select="onClientSelect" />
      </div>
    </div>

    <div v-if="tableData.length && showCharacteristicForm" class="mb-3">
      <DataTable :value="tableData" size="null" removable-sort scrollable scroll-height="450px" table-style="min-width: 50rem">
        <Column field="characteristic" header="CHARACTERISTIC" header-class="bg-light text-dark" sortable />
        <Column header="VALUE" header-class="bg-light text-dark" sortable>
          <template #body="slotProps">
            <AutoComplete
              :model-value="slotProps.data.value" :suggestions="charValSuggestions" option-label="label"
              placeholder="Enter value" fluid input-class="p-inputtext-sm"
              :panel-class="charValSuggestions.length === 0 ? 'd-none' : ''"
              @update:model-value="(val) => onCharValInput(val, slotProps.data)"
              @complete="searchCharVal($event.query, slotProps.data)"
              @item-select="(e) => onCharValSelect(e, slotProps.data)" />
          </template>
        </Column>
      </DataTable>
    </div>

    <div class="row justify-content-center">
      <div class="col">
        <button type="submit" class="btn btn-primary btn-sm w-100" :disabled="loading || searching">
          <span v-if="loading || searching" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ (loading || searching) ? 'Searching...' : 'Search' }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { AutoComplete } from "primevue/autocomplete";
import standarItemNameService from '@/services/dictionary/standarItemNameService';
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import characteristicItemNameService from '@/services/dictionary/characteristicItemNameService';
import clientService from "@/services/generalMasterData/clientService";
import api from "@/helpers/api";

export default {
  name: 'CharacteristicSearchForm',
  components: {
    AutoComplete,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return {
      clientCode: '',
      companyCode: '',
      formData: {
        select: [],
        item_type: '',
        client: '',
      },
      selectedIncObject: null,
      allIncData: [],
      // Client autocomplete data (cuma tampil untuk companyCode MIND-ID)
      selectedClientObject: null,
      allClientData: [],
      clientSuggestions: [],
      tableData: [],
      sapSuggestions: [],
      charValSuggestions: [],
      isSearching: false,
      showCharacteristicForm: false,
      // Item Type options (dari API)
      itemTypeOptions: [],
      // State loading lokal saat memanggil endpoint search
      searching: false,
      // clientOptions: [],
    };
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
    this.companyCode = userAuth?.user?.company_code || '';
    this.fetchItemTypes();
  },
  computed: {
    incDisplayValue() {
      return this.selectedIncObject?.label || "";
    },
    clientDisplayValue() {
      return this.selectedClientObject?.label || this.formData.client || "";
    },
  },
  methods: {
    // Fetch item type options from API
    async fetchItemTypes() {
      try {
        const response = await generalMasterDataService.list('item_type');
        if (response.data && Array.isArray(response.data.data)) {
          this.itemTypeOptions = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching item types:', error);
      }
    },
    // Autocomplete search over Item Name (INC) via API
    async searchInc(query) {
      if (!query || query.length < 2) {
        this.sapSuggestions = [];
        return;
      }

      try {
        const response = await standarItemNameService.list({ search: query, limit: 100 });
        const items = response.data?.data?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          this.allIncData = items;
          this.sapSuggestions = items.map(item => item.label);
        } else {
          this.allIncData = [];
          this.sapSuggestions = [];
        }
      } catch (error) {
        console.error('Error searching item names:', error);
        this.allIncData = [];
        this.sapSuggestions = [];
      }
    },
    onIncInput(value) {
      // Update status pencarian berdasarkan panjang input
      this.isSearching = !!(value && value.length >= 2);

      if (!this.isSearching) {
        this.sapSuggestions = [];
      }

      const match = this.allIncData.find(u => u.label === value);
      if (match) {
        this.selectIncItem(match);
      } else {
        this.selectedIncObject = null;
        this.formData.select = [];
        // INC diedit/dihapus -> reset item type & tabel characteristic
        this.resetCharacteristic();
      }
    },
    onIncSelect(event) {
      const selectedLabel = event?.value;
      const match = this.allIncData.find(u => u.label === selectedLabel);
      if (match) {
        this.selectIncItem(match);
      }
    },
    // Set INC terpilih + reset pilihan item type & tabel (karena INC berubah)
    selectIncItem(match) {
      this.selectedIncObject = match;
      this.formData.select = [match.id];
      this.isSearching = false; // Setelah item dipilih, bukan lagi dalam mode pencarian
      this.resetCharacteristic();
    },
    // Kosongkan item type & tabel characteristic
    resetCharacteristic() {
      this.formData.item_type = '';
      this.tableData = [];
      this.showCharacteristicForm = false;
    },
    // Item type berubah -> panggil API characteristic-item-names/search
    async handleTypeChange() {
      // Butuh INC & item type terpilih
      if (!this.selectedIncObject || !this.formData.item_type) {
        this.tableData = [];
        this.showCharacteristicForm = false;
        return;
      }

      try {
        const response = await characteristicItemNameService.listByItemName({
          limit: 100,
          page: 1,
          sort_by: 'sequence', 
          sort_order: 'asc',
          inc: this.selectedIncObject.inc,
          item_type: this.formData.item_type,
        });
        const items = response.data?.data?.data || [];
        this.tableData = items.map(row => ({
          // Label untuk kolom tabel (nama characteristic)
          characteristic: row.characteristic?.characteristic || row.characteristic_code,
          // Code characteristic (CHR....) untuk payload search
          characteristic_code: row.characteristic_code,
          // Code characteristic-item-name untuk lookup nilai (char-values)
          cin_code: row.code,
          // Nilai yang ditampilkan + code-nya (CINV....) untuk payload search
          value: '',
          cinv_code: '',
        }));
        this.showCharacteristicForm = this.tableData.length > 0;
      } catch (error) {
        console.error('Error fetching characteristics:', error);
        this.tableData = [];
        this.showCharacteristicForm = false;
      }
    },
    // Autocomplete char value per baris -> API char-values (cin_code = code characteristic).
    // Suggestion disimpan sebagai objek { label, value } agar bisa menyimpan
    // cinv_code (CINV....) untuk payload, sementara label ditampilkan ke user.
    async searchCharVal(query, row) {
      if (!row || !row.cin_code) {
        this.charValSuggestions = [];
        return;
      }
      try {
        const response = await characteristicItemNameService.searchCinVal({
          cin_code: row.cin_code,
          characteristic_value: query || '',
        });
        const items = response.data?.data?.data || [];
        this.charValSuggestions = items.map(item => ({
          label: item.characteristic_value,
          value: item.cinv_code,
        }));
      } catch (error) {
        console.error('Error searching char values:', error);
        this.charValSuggestions = [];
      }
    },
    // User mengetik manual pada kolom value -> simpan teks, cocokkan ke cinv_code
    // bila teks sama persis dengan salah satu suggestion.
    onCharValInput(val, row) {
      // Saat item dipilih, PrimeVue bisa mengirim objek { label, value }.
      if (val && typeof val === 'object') {
        row.value = val.label || '';
        row.cinv_code = val.value || '';
        return;
      }
      row.value = val;
      const match = this.charValSuggestions.find(s => s.label === val);
      row.cinv_code = match ? match.value : '';
    },
    // User memilih value dari dropdown -> simpan label & cinv_code-nya.
    onCharValSelect(event, row) {
      const selected = event?.value;
      if (selected && typeof selected === 'object') {
        row.value = selected.label;
        row.cinv_code = selected.value;
      }
    },
    // Search Clients untuk autocomplete (cuma dipanggil kalau field-nya tampil, yaitu companyCode MIND-ID)
    async searchClients(query) {
      if (!query || query.length < 2) {
        this.clientSuggestions = [];
        return;
      }
      try {
        const response = await api(this, clientService.list({ search: query, limit: 100 }), false);
        const items = response.data?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          this.allClientData = items;
          this.clientSuggestions = items.map(c => c.label || c.code);
        } else {
          this.clientSuggestions = [];
          this.allClientData = [];
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        this.clientSuggestions = [];
        this.allClientData = [];
      }
    },
    onClientInput(value) {
      const matchingClient = this.allClientData.find(c => (c.label || c.code) === value);
      if (matchingClient) {
        this.selectedClientObject = matchingClient;
        this.formData.client = matchingClient.code;
      } else {
        this.selectedClientObject = null;
        this.formData.client = value;
      }
    },
    onClientSelect(event) {
      if (event.value) {
        const matchingClient = this.allClientData.find(c => (c.label || c.code) === event.value);
        if (matchingClient) {
          this.selectedClientObject = matchingClient;
          this.formData.client = matchingClient.code;
        }
      }
    },
    buildPayload() {
      const payloadReal = {};
      if (this.selectedIncObject?.inc) {
        payloadReal.inc = this.selectedIncObject.inc;
      }
      if (this.formData.item_type) {
        payloadReal.item_type = this.formData.item_type
      }
      // client cuma bisa terisi kalau field-nya sempat tampil (companyCode MIND-ID) —
      // untuk companyCode lain formData.client tetap '' sehingga key ini tidak pernah dilempar.
      if (this.formData.client) {
        payloadReal.client = this.formData.client;
      }
      const characteristics = this.tableData.filter((row) => row.cinv_code).map((row) => ({ characteristic: row.characteristic_code, characteristic_value: row.cinv_code }));
      if (characteristics.length) {
        payloadReal.characteristics = characteristics;
      }
      return payloadReal;
    },
    async onSubmit() {
      const payload = this.buildPayload();
      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
/* Standardize size for inputs, autocomplete, and selects within search form */
.search-form :deep(.p-inputtext) {
  font-size: 14px !important;
  font-family: inherit;
}

.search-form :deep(.p-autocomplete),
.search-form :deep(.p-dropdown) {
  font-size: 14px !important;
  font-family: inherit;
}

/* Autocomplete lebih lebar dari item type */
.sap-autocomplete {
  flex: 1 1 auto;
}

.sap-autocomplete :deep(.p-autocomplete),
.sap-autocomplete :deep(.p-inputtext) {
  width: 100%;
}

.item-type-select {
  flex: 0 0 200px;
  max-width: 200px;
}
</style>
